import { PublicTwoTone } from '@mui/icons-material';
import { isNonEmptyArray } from '@sortlist-frontend/utils';
import { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete';
import { components, MenuListProps, MenuProps } from 'react-select';

export type GeoPolygon = ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>;

export type Coordinates = {
  type?: string;
  coordinates?: number[];
};

export type GooglePlaceAutocompleteResponse = {
  place_id: string;
  description: string;
  types: string[];
  polygon?: GeoPolygon;
  structured_formatting: {
    main_text: string;
  };
};

export type LocationType = {
  placeId?: string;
  address?: string;
  country?: string | null;
  types?: string[];
  coordinates?: Coordinates;
  polygon?: GeoPolygon;
  iso31661?: string;
  locationName?: string;
};

export type GooglePlaceAutocompleteType = {
  types: string[];
  address: string;
  coordinates: Coordinates;
  polygon: GeoPolygon;
  google_place_id: string;
  iso31661?: string;
  locationName?: string;
  language?: string;
};

const getGeoJson = (viewport: google.maps.LatLngBounds): GeoPolygon => {
  return [
    [
      [viewport.getNorthEast().lng(), viewport.getNorthEast().lat()],
      [viewport.getNorthEast().lng(), viewport.getSouthWest().lat()],
      [viewport.getSouthWest().lng(), viewport.getSouthWest().lat()],
      [viewport.getSouthWest().lng(), viewport.getNorthEast().lat()],
      [viewport.getNorthEast().lng(), viewport.getNorthEast().lat()],
    ],
  ];
};

/** @deprecated Should be re-implemented with use-places-autocomplete methods */
export const getExtendedLocation = async (location: GooglePlaceAutocompleteResponse): Promise<LocationType> => {
  const { description, place_id: placeId, types, polygon } = location;

  return geocodeByPlaceId(placeId).then((results) => {
    const data = results[0];
    const country = data?.address_components?.find(({ types }) => types.indexOf('country') >= 0);
    const iso31661 = country?.short_name;
    const isCountry = data?.types?.includes('country');
    return getLatLng(data).then(({ lat, lng }) => {
      return {
        address: description,
        coordinates: { type: 'Point', coordinates: [lng, lat] },
        placeId,
        types,
        country: iso31661,
        polygon: polygon || getGeoJson(data.geometry.viewport),
        locationName: getLocationName(location),
        ...(isCountry && { iso31661 }),
      };
    });
  });
};

export const isLocation = (value: GooglePlaceAutocompleteResponse | LocationType): value is LocationType => {
  return 'placeId' in value;
};

export const isGooglePlaceAutocompleteResponse = (
  value: GooglePlaceAutocompleteResponse | LocationType,
): value is GooglePlaceAutocompleteResponse => {
  return 'place_id' in value;
};

export const isGoogleMyBusiness = (types: string[]) => {
  return types.includes('establishment');
};

export const getLocationName = (location: GooglePlaceAutocompleteResponse | LocationType) => {
  if (location == null || !isGooglePlaceAutocompleteResponse(location)) return;
  if (!isGoogleMyBusiness(location.types)) return;
  return location.structured_formatting.main_text;
};

export const getExtendedDefaultLocation = async (location: LocationType): Promise<LocationType> => {
  const { address, placeId, polygon } = location;

  if (!placeId) return Promise.resolve(location);

  return geocodeByPlaceId(placeId).then((results) => {
    const data = results[0];
    const country = data?.address_components?.find(({ types }) => types.indexOf('country') >= 0);
    const iso31661 = country?.short_name;
    const isCountry = data?.types?.includes('country');
    return getLatLng(data).then(({ lat, lng }) => {
      return {
        address,
        coordinates: { type: 'Point', coordinates: [lng, lat] },
        placeId,
        types: data?.types,
        country: iso31661,
        polygon: polygon || getGeoJson(data.geometry.viewport),
        ...(isCountry && { iso31661 }),
      };
    });
  });
};

export const valueComposer = (value: LocationType) => ({ value, label: value.address ?? '' });

export const Menu = (props: MenuProps<unknown, false>) => {
  if (!isNonEmptyArray(props.options)) return null;
  return <components.Menu {...props}>{props.children}</components.Menu>;
};

const anywhere = (label: string) => ({
  place_id: undefined,
  address: label,
  coordinates: undefined,
  country: undefined,
  types: ['world'],
});

export const MenuList = (
  props: MenuListProps<unknown, false>,
  handleChange: (location: LocationType) => void,
  anywhereLabel: string,
) => {
  const anywhereOption = anywhere(anywhereLabel);

  return (
    <components.MenuList {...props}>
      {props.children}
      {isNonEmptyArray(props.children) && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div
          onClick={() => handleChange(anywhereOption)}
          className="px-4 py-3 layout-row layout-align-start-center cursor-pointer location-anywhere">
          <PublicTwoTone style={{ fontSize: 16 }} className="mr-2" />
          <div className="flex-1">{anywhereOption.address}</div>
        </div>
      )}
    </components.MenuList>
  );
};

export const includePolygon = (location: GoogleLocation): SearchLocationData => {
  const { formatted_address, polygon, geometry, address_components } = location;
  const bestMatch = address_components?.[0];
  const isCountry = bestMatch?.types?.includes('country');

  return {
    label: formatted_address,
    value: {
      ...location,
      ...(isCountry && { iso31661: bestMatch.short_name }),
      polygon: polygon ?? getGeoJson(geometry.viewport),
    },
  };
};

export type GoogleLocationData = {
  label: string;
  value: GoogleLocation;
};

export type GoogleLocation = {
  address_components: {
    short_name: string;
    types: string[];
  }[];
  formatted_address: string;
  place_id: string;
  geometry: {
    viewport: google.maps.LatLngBounds;
  };
  polygon?: GeoPolygon;
};

export type SearchLocation = {
  formatted_address: string;
  geometry: {
    viewport: google.maps.LatLngBounds;
  };
  polygon: GeoPolygon;
  iso31661?: string;
};

export type SearchLocationData = {
  label: string;
  value: SearchLocation;
};

export const getSingleLocationData = (placeId: string): Promise<SearchLocationData | undefined> => {
  return geocodeByPlaceId(placeId)
    .catch((err) => undefined)
    .then((results) => (isNonEmptyArray(results) ? includePolygon(results[0] as GoogleLocation) : undefined));
};

export const exportLocation = (location?: GooglePlaceAutocompleteType | null): LocationType => {
  const { google_place_id, ...rest } = location ?? {};
  return { ...rest, placeId: google_place_id };
};

export const importLocation = (location: LocationType): GooglePlaceAutocompleteType => {
  const { placeId, ...rest } = location ?? {};
  return {
    ...rest,
    google_place_id: placeId,
  } as GooglePlaceAutocompleteType;
};
